<template>
  <PModal
    title="Dupliquer ce parapheur"
    :model-value="modelValue"
    @update:model-value="emit('update:modelValue', $event)"
    size="lg"
  >
    <template #activator> <span class="hidden"></span> </template>
    <template #default>
      <div v-if="isLoading" class="h-full flex flex-col items-center justify-center">
        <video src="/cloning.mp4" autoplay loop muted class="h-[240px] w-auto translate-x-[-1px]" />

        <div>{{ loadingTextes[loadingIndex] }}</div>

        <Progressbar class="mt-4 w-full" />
      </div>
      <div v-else>
        <PInput label="Nouveau titre du parapheur" v-model="newName"></PInput>

        <div class="flex flex-col gap-2">
          <div>Quelles parties du parapheur souhaitez-vous dupliquer :</div>
          <PCheckbox label="Flux" v-model="cloneStream" :hide-messages="true" />
          <PCheckbox label="Personnes en copie" v-model="cloneCC" :hide-messages="true" />
          <!-- <PCheckbox
          label="Commentaires"
          v-model="cloneComments"
          :hide-messages="true"
        /> -->
          <PCheckbox label="Métadonnées" v-model="cloneMetadata" :hide-messages="true" />
          <PCheckbox label="Documents" v-model="cloneDocuments" :hide-messages="true" />
        </div>
      </div>
    </template>
    <template #actions>
      <div v-if="!isLoading" class="flex gap-2 justify-end -mr-2">
        <PButton mode="secondary" @click="cancel()"> Annuler </PButton>
        <PButton mode="primary" @click="clone()" :disabled="isLoading"> Dupliquer </PButton>
      </div>
    </template>
  </PModal>
</template>

<script setup lang="ts">
import {
  PModal,
  PButton,
  PInput,
  usePastaRouter,
  useAlertManager,
  AlertType,
  AlertDuration,
  PCheckbox,
} from "@pasta/box-v2";
import { ref, watch } from "vue";
import { type LetterFileDto } from "../dto/letter-file.dto";
import { useLetterFileStore } from "../stores/letter-file.store";
import Progressbar from "./Progressbar.vue";

interface Props {
  modelValue: boolean;
  letterFile?: LetterFileDto | null;
}
const props = defineProps<Props>();
const emit = defineEmits<{ (e: "update:modelValue", value: boolean): void }>();

const letterFileStore = useLetterFileStore();
const pastaRouter = usePastaRouter();

const isLoading = ref(false);
const newName = ref("");
const cloneStream = ref(false);
const cloneCC = ref(false);
const cloneComments = ref(false);
const cloneDocuments = ref(true);
const cloneMetadata = ref(true);
const loadingIndex = ref(0);

const loadingTextes = [
  "Cette opération peut prendre un peu de temps...",
  "Savez-vous que vous pouvez mettre des utilisateurs en copie d'un parapheur ?",
  "Vous avez besoin d'aide consulter le tutoriel !",
  "La duplication d'un parapheur peut prendre plusieurs minutes...",
];

watch(
  () => props.modelValue,
  () => {
    newName.value = `[Dupliqué] ${props.letterFile?.name}`;
    cloneStream.value = false;
    cloneCC.value = false;
    cloneComments.value = false;
    cloneMetadata.value = false;
    cloneDocuments.value = true;
  },
  { immediate: true }
);

function cancel() {
  emit("update:modelValue", false);
}

async function loopOnLoadingText() {
  loadingIndex.value = 0;
  while (isLoading.value) {
    await new Promise(r => setTimeout(r, 4000));
    loadingIndex.value = (loadingIndex.value + 1) % loadingTextes.length;
  }
}

async function clone() {
  isLoading.value = true;

  loopOnLoadingText();

  const { chronoNumber } = await letterFileStore.clone(props.letterFile!.id, {
    name: newName.value,
    cloneStream: cloneStream.value,
    cloneCC: cloneCC.value,
    cloneComments: cloneComments.value,
    cloneDocuments: cloneDocuments.value,
    cloneMetadata: cloneMetadata.value,
  });

  await pastaRouter.push(`/letter-file/${chronoNumber}`);
  useAlertManager().createAlert({
    message: "Le parapheur a bien été dupliqué",
    type: AlertType.SUCCESS,
    duration: AlertDuration.LONG,
  });
  isLoading.value = false;
  emit("update:modelValue", false);
}
</script>
